import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import * as immutable from 'immutable';
import {List} from 'immutable';
import StatTabContent from 'components/dashboard/StatTabContent';
import ExecTrendChartContainer from 'components/pure/metrics/ExecTrendChartContainer';
import Dialog from 'components/pure/utils/Dialog';
import TopicRollupSummaryDrillDown from 'components/dashboard/rollups/topic/TopicRollupSummaryDrillDown';
import {
  makeChartLabel,
  percentage,
  TOPIC_ROLLUP_BAR_COLOR,
  TOPIC_ROLLUP_SUBTABS,
  TOPIC_ROLLUP_SUMMARY_TEAM,
} from 'components/dashboard/rollups/topic/common';
import ExecDashboardSource from 'sources/ExecDashboardSource';
import GenericLink from 'components/GenericLink';
import ExecDashboardActions from 'actions/ExecDashboardActions';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import './TopicRollupSummaryTab.styl';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';

function usageTooltipContent(usage) {
  if (!usage) {
    return 'Not used by any departments or teams';
  }
  const {departmentNames, teamNames} = usage;
  return (
    <div className="topic-name-tooltip-content">
      <ul>
        {departmentNames.map((name, idx) => (
          <li key={idx}>Department: {name}</li>
        ))}
        {teamNames.map((name, idx) => (
          <li key={idx + departmentNames.size}>Team: {name}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Topic rollup summary tab content.
 *
 * Bars for stats rolled up by dashboard trigger, with a drill down into team, then into reps.
 */
export default class TopicRollupSummaryTab extends React.PureComponent {
  static propTypes = {
    activeSubtabConfig: PropTypes.object,
    activeSubtabId: PropTypes.string,
    adminId: PropTypes.string,
    coachId: PropTypes.string,
    conversations: PropTypes.instanceOf(immutable.Map).isRequired,
    endDate: PropTypes.instanceOf(Moment).isRequired,
    location: PropTypes.object.isRequired, // React router location.
    profileUsageByTriggerRuleId: PropTypes.instanceOf(immutable.Map).isRequired,
    // Map<string, List> (keys are subtab ids)
    summaryStats: PropTypes.instanceOf(immutable.Map).isRequired,
    startDate: PropTypes.instanceOf(Moment).isRequired,
    teams: PropTypes.instanceOf(immutable.List).isRequired, // List<Team>
    triggerNameFromStat: PropTypes.func.isRequired, // (stat) => string
  };

  constructor(props) {
    super(props);
    this.state = {
      summaryDrillDownReady: false,
      summaryDrillDownOpen: false,
      summaryDrillDownStats: List(),
      summaryDrillDownTriggerId: undefined,
    };
    this.openSummaryDrillDown = this.openSummaryDrillDown.bind(this);
    this.closeSummaryDrillDown = this.closeSummaryDrillDown.bind(this);
    this.fetchTrendData = this.fetchTrendData.bind(this);
  }

  isPercent() {
    const {activeSubtabId} = this.props;
    return activeSubtabId === TOPIC_ROLLUP_SUBTABS.PERCENT;
  }

  openSummaryDrillDown(stat) {
    const {endDate, startDate, teams: teamRecords, callDrillDown, conversations} = this.props;
    if (teamRecords.size === 1) return callDrillDown(stat);
    const teams = teamRecords.map(team => team.id).toJS();
    this.setState(
      {
        summaryDrillDownOpen: true,
        summaryDrillDownReady: false,
        summaryDrillDownTriggerId: stat.context,
      },
      async () => {
        const teamMetrics = await ExecDashboardSource.getTopicMetrics({
          teams,
          startDate,
          endDate,
        });
        // TODO ideally the database could do this for us.
        let summaryDrillDownStats = teamMetrics.filter(metric => metric.context === stat.context);
        // If we're looking at percentages, we need to divide the counts by the total number of
        // calls for the relevant teams.
        if (this.isPercent()) {
          summaryDrillDownStats = summaryDrillDownStats.map(metric => ({
            ...metric,
            value: percentage(metric.value, conversations.get(metric.id)),
          }));
        }
        this.setState({
          summaryDrillDownReady: true,
          summaryDrillDownStats,
        });
      }
    );
  }

  closeSummaryDrillDown() {
    this.setState({
      summaryDrillDownOpen: false,
      summaryDrillDownStats: List(),
      summaryDrillDownReady: false,
      summaryDrillDownTriggerId: undefined,
    });
  }

  /**
   * Fetch trend data.
   *
   * @param stat the stat to fetch data for. Must have a context (the topic we want a trend line
   * for).
   */
  fetchTrendData(stat) {
    const {startDate, endDate, teams} = this.props;
    const [teamId, ...additionalTeamIds] = teams.map(team => team.id);

    let trendPromise;

    if (this.isPercent()) {
      trendPromise = ExecDashboardActions.getTopicTrendPercentData({
        teamId,
        additionalTeamIds,
        startDate,
        endDate,
        context: stat.context,
      });
    } else {
      trendPromise = ExecDashboardActions.getTrendData({
        type: 'trigger',
        teamId,
        additionalTeamIds,
        startDate,
        endDate,
        context: stat.context,
      });
    }
    trendPromise.then(res => fetchMissingUsers(Object.keys(res)));
  }

  render() {
    const {
      activeSubtabConfig,
      activeSubtabId,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      location,
      summaryStats,
      onSelectSubtab,
      profileUsageByTriggerRuleId,
      tabConfigs,
      teams,
      triggerNameFromStat,
      callDrillDown,
    } = this.props;
    const {summaryDrillDownStats, summaryDrillDownOpen, summaryDrillDownReady} = this.state;

    const getLinkAndName = stat => {
      const triggerName = triggerNameFromStat(stat);
      // Keys here get turned into strings during conversion to JSON on the server. It's actually
      // convenient, because they're also strings in the context here.
      const usage = profileUsageByTriggerRuleId.get(stat.context);
      const usageTooltip = usageTooltipContent(usage);
      const singleTeam = teams.size === 1;
      return [
        <GenericTooltip delay={TOOLTIP_DELAY} tooltip={usageTooltip}>
          {singleTeam ? (
            <span className="no-link">{triggerName}</span>
          ) : (
            <GenericLink onClick={() => this.openSummaryDrillDown(stat)}>{triggerName}</GenericLink>
          )}
        </GenericTooltip>,
        triggerName,
      ];
    };

    return (
      <div className="topic-rollup-summary-tab">
        <StatTabContent
          activeTabId={TOPIC_ROLLUP_SUMMARY_TEAM.id}
          activeSubtabId={activeSubtabId}
          chartLabelFn={makeChartLabel}
          color={TOPIC_ROLLUP_BAR_COLOR}
          emptyMessage="No calls matching those criteria"
          endDate={endDate}
          fetchTrendData={this.fetchTrendData}
          getLinkAndName={getLinkAndName}
          headerStartDate={headerStartDate}
          headerEndDate={headerEndDate}
          location={location}
          onOpenDrillDown={callDrillDown}
          selectSubtab={onSelectSubtab}
          showAverage={false}
          startDate={startDate}
          stats={summaryStats.get(activeSubtabId, List())}
          tabConfigs={tabConfigs}
          trendComponent={ExecTrendChartContainer}
        />
        <Dialog
          className="topic-rollup-drilldown-dialog"
          open={summaryDrillDownOpen}
          onRequestClose={this.closeSummaryDrillDown}>
          <TopicRollupSummaryDrillDown
            adminId={this.props.adminId}
            coachId={this.props.coachId}
            color={TOPIC_ROLLUP_BAR_COLOR}
            chartLabelFn={makeChartLabel}
            endDate={endDate}
            headerStartDate={headerStartDate}
            headerEndDate={headerEndDate}
            loading={!summaryDrillDownReady}
            location={location}
            stats={summaryDrillDownStats}
            startDate={startDate}
            subtabConfig={activeSubtabConfig}
            subtabId={activeSubtabId}
            teams={teams}
            triggerId={this.state.summaryDrillDownTriggerId}
            triggerNameFromStat={triggerNameFromStat}
          />
        </Dialog>
      </div>
    );
  }
}
